import React, { useEffect } from 'react';
import { FiArrowLeft, FiPrinter } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';

const Namuna12Receipt = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const transaction = location.state?.transaction || {};
    
    const handlePrint = () => {
        window.print();
    };
    
    const handleBack = () => {
        navigate(-1);
    };

    // Hide sidebar and navbar when printing
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @media print {
                body * {
                    visibility: hidden;
                }
                .print-receipt, .print-receipt * {
                    visibility: visible;
                }
                .print-receipt {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                }
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Function to convert number to words in Marathi
    const numberToWordsMarathi = (num) => {
        const ones = ['', 'एक', 'दोन', 'तीन', 'चार', 'पाच', 'सहा', 'सात', 'आठ', 'नऊ', 'दहा', 'अकरा', 'बारा', 'तेरा', 'चौदा', 'पंधरा', 'सोळा', 'सतरा', 'अठरा', 'एकोणीस'];
        const tens = ['', '', 'वीस', 'तीस', 'चाळीस', 'पन्नास', 'साठ', 'सत्तर', 'ऐंशी', 'नव्वद'];
        const thousands = ['', 'हजार', 'लाख', 'करोड'];

        if (num === 0) return 'शून्य';

        let words = '';
        let i = 0;

        do {
            let n = num % 1000;
            num = Math.floor(num / 1000);

            if (n) {
                let str = '';
                if (n >= 100) {
                    str += ones[Math.floor(n / 100)] + 'शे ';
                    n %= 100;
                }
                if (n < 20) {
                    str += ones[n];
                } else {
                    str += tens[Math.floor(n / 10)];
                    if (n % 10) str += ' ' + ones[n % 10];
                }
                words = str + ' ' + thousands[i] + ' ' + words;
            }
            i++;
        } while (num > 0);

        return words.trim();
    };

    const amountInWords = numberToWordsMarathi(transaction.totalAmount || 0);

    const ReceiptContent = ({ type }) => (
        <div className="border-2 border-black p-4 mb-8 print:mb-0 print:break-after-page print:p-6">
            {/* Title Section */}
            <div className="text-center mb-4">
                <h1 className="text-lg font-bold mb-1">ग्रामपंचायत इनाम धामणी</h1>
                <h2 className="text-base font-semibold">नमुना १२</h2>
            </div>

            {/* Date and Voucher Details */}
            <div className="mb-4 text-sm">
                <div className="flex justify-between items-start mb-2">
                    <div>
                        <p><strong>तारीख :</strong> {transaction.date}</p>
                    </div>
                    <div className="text-right">
                        <p><strong>प्रमाणक (व्हाऊचर) क्रमांक :-</strong> {transaction.challanNo || '2'}</p>
                    </div>
                </div>
                
                <div className="flex justify-between items-start">
                    <div>
                        <p><strong>ठराव क्रमांक :</strong> {transaction.resolutionNo || '1'}</p>
                    </div>
                    <div className="text-right">
                        <p><strong>तारीख :</strong> {transaction.resolutionDate || transaction.date}</p>
                    </div>
                </div>
            </div>

            {/* Person Name */}
            <div className="mb-4 text-sm">
                <p><strong>व्यक्तीचे नाव :</strong> {transaction.personName}</p>
            </div>

            {/* Amount Details */}
            <div className="mb-4 space-y-1 text-sm">
                <p>बिलात दर्शविलेली रुपये {transaction.totalAmount || 0}.00 ची रक्कम मंजूर करण्यात येत आहे.</p>
                <p>दाव्याच्या पूर्ण फेडीबद्दल रु. {transaction.totalAmount || 0}.00 (अक्षरी) {amountInWords} रुपये फक्त ची रक्कम मिळाली.</p>
            </div>

            {/* Amount Received */}
            <div className="mb-6 text-sm">
                <p><strong>माझ्यासमोर रक्कम दिली {transaction.totalAmount || 0}.00</strong></p>
            </div>

            {/* Signature Section */}
            <div className="grid grid-cols-2 gap-6 mt-8">
                <div>
                    <div className="border-t border-black pt-1">
                        <p className="text-sm">सरपंच :</p>
                    </div>
                </div>
                <div>
                    <div className="border-t border-black pt-1">
                        <p className="text-sm">पैसे घेणाराची सही</p>
                    </div>
                </div>
            </div>

            {/* Stamp */}
            <div className="mt-6 text-center">
                <div className="inline-block border border-dashed border-gray-400 p-2">
                    <p className="text-xs">मुद्रांक</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-white p-2 print:p-4 print-receipt">
            <div className="max-w-6xl mx-auto print:max-w-full">
                {/* Header */}
                <div className="flex justify-between items-center mb-4 print:hidden">
                    <button 
                        onClick={handleBack}
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                    >
                        <FiArrowLeft /> Back
                    </button>
                    <button 
                        onClick={handlePrint}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                        <FiPrinter /> Print
                    </button>
                </div>

                {/* Two Receipts Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-2">
                    {/* First Receipt */}
                    <ReceiptContent type="original" />
                    
                    {/* Second Receipt */}
                    <ReceiptContent type="duplicate" />
                </div>
            </div>
        </div>
    );
};

export default Namuna12Receipt;
